import { useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { config } from '../shared/config';
import { currentAccessToken } from '../lib/supabase';
import { Button } from './ui';

/** Logos are served only to their owner, so fetch with the token and show a blob. */
function useAuthedImage(url: string | null | undefined): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!url) {
      setSrc(null);
      return;
    }
    let blobUrl: string | null = null;
    let live = true;
    void (async () => {
      try {
        const token = await currentAccessToken();
        const full = url.startsWith('http') ? url : `${config.apiUrl}${url}`;
        const res = await fetch(full, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok || !live) return;
        blobUrl = URL.createObjectURL(await res.blob());
        setSrc(blobUrl);
      } catch {
        /* preview only */
      }
    })();
    return () => {
      live = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [url]);
  return src;
}

/**
 * The contractor's own logo, printed at the top of the documents they generate
 * (Notice to Owner letterhead, waivers, and the rest). Their choice, their mark.
 */
export default function LogoUpload({ logoUrl, onChange }: { logoUrl: string | null | undefined; onChange: () => Promise<void> | void }) {
  const preview = useAuthedImage(logoUrl);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'ok' | 'urgent'; text: string } | null>(null);

  const upload = async (file: File) => {
    if (!/^image\/(png|jpeg)$/.test(file.type)) {
      setNote({ tone: 'urgent', text: 'Choose a PNG or JPEG image.' });
      return;
    }
    if (file.size > 1024 * 1024) {
      setNote({ tone: 'urgent', text: 'That image is over 1 MB. Save a smaller copy and try again.' });
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const form = new FormData();
      form.append('logo', file);
      await api.post('/api/me/logo', form);
      await onChange();
      setNote({ tone: 'ok', text: 'Saved. It prints on documents you generate from now on.' });
    } catch (err) {
      setNote({ tone: 'urgent', text: err instanceof ApiError ? err.message : 'Upload failed. Try again.' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.del('/api/me/logo');
      await onChange();
      setNote(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-5">
      <div className="flex h-20 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-dashed border-border bg-white text-xs text-dim">
        {preview ? <img src={preview} alt="Company logo" className="max-h-full max-w-full object-contain" /> : 'No logo'}
      </div>
      <div className="flex-1 space-y-2">
        <input
          type="file"
          accept="image/png,image/jpeg"
          aria-label="Company logo"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void upload(file);
            e.target.value = '';
          }}
          className="block w-full text-sm text-muted file:mr-3 file:rounded-lg file:border file:border-border file:bg-surface-alt file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-ink"
        />
        <p className="text-xs text-dim">PNG or JPEG, up to 1 MB. Wide logos print best; it appears at the top of your documents.</p>
        {logoUrl && (
          <Button variant="secondary" disabled={busy} onClick={() => void remove()}>
            Remove logo
          </Button>
        )}
        {note && <p className={`text-xs ${note.tone === 'ok' ? 'text-ok' : 'text-urgent'}`}>{note.text}</p>}
      </div>
    </div>
  );
}
